// @flow
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import mergeWith from 'lodash/mergeWith';
import merge from 'lodash/merge';
import isArray from 'lodash/isArray';

const safeGet = (obj, key) =>
  obj && obj.hasOwnProperty(key) ? obj[key] : undefined;

const getNestedValue = (obj, path) => {
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    current = safeGet(current, key); // Safely retrieve the next level
    if (!current) break; // Exit early if undefined or null
    if (current.hasOwnProperty('_f')) return current._f.value; // Directly return nested value if "_f" key exists
  }

  return current?.value; // Return value if it exists at the final level
};

const getBaseFieldName = (fieldName: string) => {
  if (!fieldName.includes('.')) return fieldName;
  const fieldNameArray = fieldName.split('.');

  return fieldNameArray[fieldNameArray.length - 1];
};

const getLabel = ({ label }) => {
  if (Boolean(label?.props?.id?.length)) {
    return window.app.intl.getFormattedMessageInEnglish(label.props);
  } else {
    return label;
  }
};

const getDisplayedValue = ({ optionLabel, value, variant }) => {
  if (variant === 'date' && value?._isAMomentObject) return formatDateDB(value);
  return optionLabel ?? value;
};

const getValue = ({ value, variant }) => {
  if (variant === 'date' && value?._isAMomentObject) return formatDateDB(value);
  return value;
};

const getOptionLabel = ({ options, value }) => {
  if (!options?.length) return null;

  if (Array.isArray(value)) {
    return options
      .filter((option) => value.includes(option.value))
      .map((option) => getLabel({ label: option.label ?? option.text }));
  } else {
    const selectedOption = options.find((option) => option.value === value);
    return getLabel({
      label: selectedOption?.label ?? selectedOption?.text,
    });
  }
};

const getItem = ({
  baseFieldName,
  label,
  value,
  variant,
  optionLabel,
  isAffordaPortalSpecific,
}) => {
  return {
    name: baseFieldName,
    label: getLabel({ label }),
    value: getValue({ value, variant }),
    displayedValue: getDisplayedValue({ optionLabel, value, variant }),
    isAffordaPortalSpecific: isAffordaPortalSpecific ?? false,
  };
};

const getFieldsArrayAndMap = (fields = []) => {
  const fieldsArray = Array.isArray(fields)
    ? fields
    : Object.values(fields).flatMap((field) => field);

  const fieldsMap = fieldsArray.reduce((acc, field) => {
    acc[field.name] = field;
    return acc;
  }, {});

  return {
    fieldsArray,
    fieldsMap,
  };
};

export const getNewFormState = ({ fields, newState = {}, prevState = {} }) => {
  const { fieldsArray } = getFieldsArrayAndMap(fields);

  const readOnlyFieldNames =
    fieldsArray?.filter(({ readOnly }) => readOnly)?.map(({ name }) => name) ??
    [];

  const customizer = (objValue: any, srcValue: any, key: string) => {
    if (readOnlyFieldNames.includes(key)) {
      return objValue; // Prioritize the value from the first object (prevState)
    }

    // Handle arrays: Use newState if the lengths differ
    if (isArray(objValue) && isArray(srcValue)) {
      if (objValue.length !== srcValue.length) {
        return srcValue; // Use newState array if lengths differ
      }
      return srcValue ?? objValue; // Otherwise, preserve newState
    }

    if (srcValue && typeof srcValue === 'object') {
      if (srcValue._isAMomentObject) {
        return formatDateDB(srcValue);
      } else {
        // Recursively process nested objects and arrays
        const newValue = isArray(srcValue) ? [] : {};
        let hasChanges = false; // Flag to track if any changes were made

        for (const k in srcValue) {
          const nestedValue = customizer(objValue?.[key]?.[k], srcValue[k], k);
          newValue[k] = nestedValue;
          if (nestedValue !== srcValue[k]) {
            hasChanges = true; // Set flag if a change occurred
          }
        }
        return hasChanges ? newValue : srcValue; // Return original if no changes
      }
    }

    return srcValue !== undefined ? srcValue : objValue;
  };

  return mergeWith({}, prevState, newState, customizer);
};

export const getNewFormValues = ({ control, fields, prevValues }) => {
  const newFormValues = {};

  if (!control || !fields) return prevValues ?? newFormValues;

  const { fieldsMap } = getFieldsArrayAndMap(fields);

  const mountedFieldNames = Array.from(control._names.mount);

  mountedFieldNames.forEach((fieldName) => {
    const baseFieldName = getBaseFieldName(fieldName);
    if (fieldsMap[fieldName]) {
      const { id, label, options, variant, readOnly, isAffordaPortalSpecific } =
        fieldsMap[fieldName];

      if (!readOnly) {
        const value = getNestedValue(control._fields, fieldName);
        const optionLabel = getOptionLabel({ options, value });

        const item = getItem({
          baseFieldName,
          label,
          optionLabel,
          value,
          variant,
          isAffordaPortalSpecific,
        });

        if (id) {
          newFormValues[id] = {
            ...newFormValues[id],
            [baseFieldName]: item,
          };
        } else {
          newFormValues[baseFieldName] = item;
        }
      }
    }
  });

  return merge({}, prevValues, newFormValues);
};
